import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '@/contexts/LanguageContext';
import { api } from '@/lib/api';
import notice from '@/lib/contentConsentNotice.json';

export const contentChoicesEnabled = process.env.REACT_APP_CONTENT_CONSENTS_ENABLED === '1';
const emptyContext = { categories: [], version: notice.version, revision: 0 };
export const ordinaryContent = () => ({ ...emptyContext, categories: [] });
const title = (scope, lang) => scope === 'profile' ? (lang === 'it' ? 'Biografia e foto' : 'Bio and photos') : (lang === 'it' ? 'Messaggi e allegati' : 'Messages and attachments');
const fail = lang => lang === 'it' ? 'Non è stato possibile salvare. Ricarica le scelte prima di riprovare.' : 'Could not save. Reload your choices before trying again.';

export function ContentChoicesPrompt() {
  const { lang } = useI18n();
  if (!contentChoicesEnabled) return null;
  const it = lang === 'it';
  return <aside className="nvl-card p-4 my-4" data-testid="content-choices-prompt">
    <p className="font-medium">{it ? 'Scegli come usare i dati delicati del profilo' : 'Choose how sensitive profile data is used'}</p>
    <p className="mt-2 text-sm" style={{ color: 'hsl(var(--nvl-ink-soft))' }}>
      {it ? 'Puoi aggiungere bio e foto normalmente. Se desideri inserire informazioni delicate su di te, scegli prima le categorie che autorizzi a mostrare agli altri iscritti.' : 'You can add your bio and photos normally. If you want to include sensitive information about yourself, first choose the categories you authorise us to show to other members.'}
    </p>
    <p className="mt-2 text-xs" style={{ color: 'hsl(var(--nvl-ink-soft))' }}>
      {it ? 'Per contenuti ordinari non devi attivare alcuna opzione e potrai modificare le scelte in qualsiasi momento.' : 'No option is needed for ordinary content, and you can change your choices at any time.'}
    </p>
    <Link to="/app/settings/privacy#content-consents" className="nvl-btn nvl-btn-outline text-sm mt-4">{it ? 'Scegli le opzioni' : 'Choose options'}</Link>
  </aside>;
}

export default function ContentConsents({ onSaved }) {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({});
  const [confirm, setConfirm] = useState(null);
  const [status, setStatus] = useState('');
  const load = useCallback(async () => {
    setError('');
    try {
      const r = await api.get('/profile/content-consents');
      setData(r.data);
      setDraft(Object.fromEntries(['profile', 'messages'].map(key => [key, r.data.choices[key]?.categories || []])));
      setConfirm(null);
    } catch {
      setData(null);
      setError(fail(lang));
    }
  }, [lang]);
  useEffect(() => { if (contentChoicesEnabled) load(); }, [load]);
  if (!contentChoicesEnabled) return null;
  const save = async scope => {
    const removed = (data.choices[scope]?.categories || []).filter(category => !draft[scope].includes(category));
    if (removed.length && confirm !== scope) { setConfirm(scope); return; }
    setBusy(true); setError(''); setStatus('');
    try {
      await api.patch(`/profile/content-consents/${scope}`, { categories: draft[scope], version: data.notice.version, revision: data.revision, language: lang });
      await load();
      await onSaved?.();
      setStatus(removed.length ? (lang === 'it' ? 'Scelta salvata. I contenuti contrassegnati sono stati ritirati; la cancellazione definitiva richiede la verifica delle conservazioni.' : 'Choice saved. Marked content has been withdrawn; final erasure requires preservation review.') : (lang === 'it' ? 'Scelta salvata.' : 'Choice saved.'));
    } catch {
      setError(fail(lang)); setConfirm(null);
    } finally {
      setBusy(false);
    }
  };
  return <section id="content-consents" className="nvl-card p-6 my-6 space-y-4" data-testid="content-consents" style={{ scrollMarginTop: '1.5rem' }}>
    <h2 className="text-2xl">{lang === 'it' ? 'Dati delicati nei tuoi contenuti' : 'Sensitive data in your content'}</h2>
    <p className="text-sm">{notice.withdrawal[lang]}</p>
    <p className="text-sm">{lang === 'it' ? 'Queste scelte riguardano solo informazioni su di te. Non inserire dati delicati di altre persone. Non autorizzano pubblicità mirata né deduzioni dai tuoi contenuti.' : 'These choices only cover information about you. Do not include sensitive data about other people. They do not authorise targeted advertising or inferences from your content.'}</p>
    {data && ['profile', 'messages'].map(scope => <fieldset disabled={busy} key={scope} className="border rounded p-4 space-y-3">
      <legend>{title(scope, lang)}</legend><p className="text-sm">{data.notice[scope][lang]}</p>
      {Object.entries(data.notice.categories).map(([key, label]) => <label className="flex gap-2 text-sm" key={key}>
        <input type="checkbox" aria-label={`${title(scope, lang)}: ${label[lang]}`} checked={(draft[scope] || []).includes(key)} onChange={event => { setConfirm(null); setDraft({ ...draft, [scope]: event.target.checked ? [...draft[scope], key] : draft[scope].filter(value => value !== key) }); }} />{label[lang]}
      </label>)}
      {confirm === scope && <p role="alert" className="text-sm">{lang === 'it' ? 'Saranno ritirati interamente i testi e le immagini contrassegnati con le categorie rimosse. Confermi?' : 'Whole texts and images marked with removed categories will be withdrawn. Confirm?'}</p>}
      <button type="button" className="nvl-btn nvl-btn-outline" onClick={() => save(scope)}>{confirm === scope ? (lang === 'it' ? 'Conferma ritiro' : 'Confirm withdrawal') : (lang === 'it' ? 'Salva questa scelta' : 'Save this choice')}</button>
    </fieldset>)}
    {data?.profile_withdrawals_pending > 0 && <p className="text-sm">{lang === 'it' ? `Contenuti del profilo ritirati in attesa di verifica: ${data.profile_withdrawals_pending}.` : `Withdrawn profile items awaiting review: ${data.profile_withdrawals_pending}.`}</p>}
    <button type="button" disabled={busy} className="underline text-sm" onClick={load}>{lang === 'it' ? 'Ricarica scelte' : 'Reload choices'}</button>
    <p role="alert">{error}</p><p role="status">{status}</p>
  </section>;
}

export function ContentDeclaration({ scope, value, onChange, disabled = false }) {
  const { lang } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get('/profile/content-consents');
      setData(r.data); setError(false);
      onChange(current => ({ ...current, version: r.data.notice.version, revision: r.data.revision }));
    } catch {
      setError(true); setData(null);
    }
  }, [onChange]);
  useEffect(() => { if (contentChoicesEnabled) load(); }, [scope, load]);
  if (!contentChoicesEnabled) return null;
  const allowed = data?.choices[scope]?.categories || [];
  return <details className="my-3 text-sm w-full" data-testid={`content-declaration-${scope}`}>
    <summary>{lang === 'it' ? 'Questo contenuto contiene informazioni delicate su di me' : 'This content includes sensitive information about me'}</summary>
    <p className="my-2">{lang === 'it' ? 'Se presenti, indica le categorie di questo testo o foto. Per un contenuto ordinario lascia tutto deselezionato. Non inserire informazioni delicate su altre persone.' : 'If present, mark the categories in this text or photo. Leave all unchecked for ordinary content. Do not include sensitive information about other people.'}</p>
    {Object.entries(notice.categories).map(([key, label]) => <label className="flex gap-2" key={key}>
      <input type="checkbox" checked={value.categories.includes(key)} disabled={disabled || !data || (!allowed.includes(key) && !value.categories.includes(key))} onChange={event => onChange({ ...value, categories: event.target.checked ? [...value.categories, key] : value.categories.filter(category => category !== key) })} />{label[lang]}
    </label>)}
    <Link className="underline" to="/app/settings/privacy#content-consents">{lang === 'it' ? 'Gestisci prima il consenso in Privacy' : 'Manage consent in Privacy first'}</Link>
    <button type="button" disabled={disabled} className="underline ml-3" onClick={load}>{lang === 'it' ? 'Aggiorna scelte' : 'Refresh choices'}</button>
    {error && <p role="alert">{lang === 'it' ? 'Scelte non disponibili. Non inviare contenuti delicati finché non riesci a ricaricarle.' : 'Choices unavailable. Do not send sensitive content until you can reload them.'}</p>}
  </details>;
}
